import type { ApiResult, RequestOptions } from '@/services/apiClient';
import { apiClient, apiOrigin } from '@/services/apiClient';
import type { FinanceCommandCenterPayload } from '@/features/finance/financeCommandCenter.types';
import type { FinanceInvoicePage } from '@/features/finance/financeInvoices.types';
import type { OpsSummary, OwnerSummary } from './personaDashboardsClient';
import type { ProjectHealthSource } from '@/features/projects/projects.types';

export interface ExecutiveSource<T> {
  state: 'available' | 'restricted' | 'unavailable';
  label: string;
  scope: string;
  assembledAt: string | null;
  note: string;
  data: T | null;
}

export interface ExecutiveReadModel {
  version: 1;
  assembledAt: string;
  finance: ExecutiveSource<FinanceCommandCenterPayload>;
  owner: ExecutiveSource<Omit<OwnerSummary, 'money'>>;
  operations: ExecutiveSource<OpsSummary>;
  invoices?: ExecutiveSource<{
    generatedAt: string;
    ar: FinanceInvoicePage | null;
    ap: FinanceInvoicePage | null;
  }>;
  growth: ExecutiveSource<{
    generatedAt: string;
    leads: {
      total: number;
      byStatus: Array<{ status: string; count: number }>;
      dueWithin30Days: number;
      undated: number;
      latestRecordAt: string | null;
    } | null;
    estimates: {
      total: number;
      byStatus: Array<{ status: string; count: number }>;
      latestRecordAt: string | null;
    } | null;
    note: string;
  }>;
}

export type RiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
export type AlertStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'DISMISSED';
export type ProjectStatus = 'PLANNING' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
export type RecommendationStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'ASSIGNED'
  | 'DISMISSED'
  | 'EXECUTED';

export interface CommandCenterFilters {
  search?: string;
  sector?: string;
  region?: string;
  risk?: RiskLevel | '';
}

export interface CommandCenterKpi {
  id:
    | 'portfolioValue'
    | 'backlog'
    | 'grossMargin'
    | 'cashPosition'
    | 'activeProjects'
    | 'criticalRiskProjects'
    | 'openAlerts'
    | 'safetyScore'
    | 'scheduleHealth'
    | 'procurementRisk'
    | 'laborUtilization';
  label: string;
  value: number | null;
  unit: 'currency' | 'count' | 'percent';
  formatted: string;
  delta: string;
  trend: 'up' | 'down' | 'flat';
  accent: 'primary' | 'success' | 'warning' | 'critical' | 'info' | 'ai' | 'brand';
  source: string;
  drilldown: string;
}

export interface CommandCenterProjectMarker {
  id: string;
  projectNumber: string;
  name: string;
  clientName: string | null;
  sector: string | null;
  status: ProjectStatus;
  phase: string;
  riskLevel: RiskLevel;
  healthScore: number | null;
  /** Older cached responses without source history must not establish a score. */
  healthSource?: ProjectHealthSource;
  contractValue: number | null;
  budgetCurrent: number | null;
  actualCost: number | null;
  forecastAtCompletion: number | null;
  marginVariance: number | null;
  updatedAt?: string;
  substantialCompletion?: string | null;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
  openAlerts: number;
  criticalAlerts: number;
}

export interface CommandCenterAlertItem {
  id: string;
  title: string;
  message: string;
  category: string;
  severity: RiskLevel;
  status: AlertStatus;
  source: string;
  project: { id: string; name: string; projectNumber: string } | null;
  createdAt: string;
}

export interface CommandCenterAdvisorItem {
  id: string;
  title: string;
  summary: string;
  severity: RiskLevel;
  status: RecommendationStatus;
  confidenceScore: number | null;
  estimatedFinancialImpact: number | null;
  estimatedScheduleImpactDays: number | null;
  recommendedAction: string;
  evidence: unknown;
  agent: { id: string; name: string; key: string };
  project: { id: string; name: string; projectNumber: string } | null;
  createdAt: string;
}

export interface CommandCenterPayload {
  executive?: ExecutiveReadModel;
  coverage?: {
    canReadProjects: boolean;
    canReadFinance: boolean;
    canReadRecommendations: boolean;
    activeAlertCount: number;
    openAlertCount: number;
    recommendationCount: number | null;
    shownAlertCount: number;
    shownRecommendationCount: number;
    projectCohort: string;
  };
  generatedAt: string;
  tenant: { id: string; name: string; slug: string };
  filters: Required<Pick<CommandCenterFilters, 'search' | 'sector' | 'region' | 'risk'>>;
  featureFlags: { commandCenterV2: boolean };
  permissions: {
    canReadDashboard: boolean;
    canExportReport: boolean;
    canGenerateAdvisorBrief: boolean;
  };
  summary: {
    activeProjects: number | null;
    totalProjects: number | null;
    openAlerts: number;
    activeAgents: number | null;
    portfolioValue: number | null;
    portfolioHealth: number | null;
    highestRiskLevel: RiskLevel | 'NONE' | null;
  };
  kpis: CommandCenterKpi[];
  projectMap: {
    bounds: {
      minLatitude: number;
      maxLatitude: number;
      minLongitude: number;
      maxLongitude: number;
    } | null;
    markers: CommandCenterProjectMarker[];
  };
  financials: {
    cashFlowForecast: Array<{ period: string; inflow: number; outflow: number; net: number }>;
    bySector: Array<{
      sector: string;
      contractValue: number | null;
      forecastAtCompletion: number | null;
      margin: number | null;
    }>;
    portfolioMargin: number | null;
  };
  projectPerformance: {
    byRisk: Array<{ riskLevel: RiskLevel; count: number; contractValue: number | null }>;
    byPhase: Array<{ phase: string; count: number; contractValue: number | null }>;
    healthDistribution: Array<{ band: string; count: number }>;
    healthCoverage?: { recordedCount: number; unavailableCount: number; totalProjects: number } | null;
  };
  alerts: CommandCenterAlertItem[];
  advisor: {
    generatedAt: string;
    recommendations: CommandCenterAdvisorItem[];
  };
  liveSites: Array<{
    id: string;
    projectNumber: string;
    name: string;
    location: string;
    feedStatus: 'live' | 'delayed' | 'offline' | 'unavailable';
    latestSignal: string;
    riskLevel: RiskLevel;
    healthScore: number | null;
  }>;
  sourceRecords: {
    projects: CommandCenterProjectMarker[];
    alerts: CommandCenterAlertItem[];
    recommendations: CommandCenterAdvisorItem[];
  };
  availableActions: Array<{
    id: 'refresh' | 'generate_advisor_brief' | 'export_report';
    label: string;
    enabled: boolean;
    reason: string | null;
  }>;
}

export interface CommandCenterActionResult {
  generatedAt: string;
  notificationId: string;
  title: string;
  summary: string;
  recommendationCount: number;
  openAlertCount: number;
}

export interface CommandCenterExportResult {
  generatedAt: string;
  reportId: string;
  title: string;
  downloadUrl: string;
  downloadUrlExpiresAt: string;
  fileName: string;
  sha256: string | null;
  pageCount: number | null;
  sourceScope: string;
  filters: CommandCenterPayload['filters'];
}

function expectData<T>(result: ApiResult<T>): T {
  if (!result.ok || !result.data) {
    throw new Error(result.error ?? 'API request failed');
  }
  return result.data;
}

function queryString(filters: CommandCenterFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'string' && value.trim().length > 0) {
      params.set(key, value.trim());
    }
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

async function getCommandCenter(
  filters: CommandCenterFilters,
  options?: RequestOptions,
): Promise<CommandCenterPayload> {
  return expectData(
    await apiClient.get<CommandCenterPayload>(
      `${apiOrigin}/api/v1/dashboard/command-center${queryString(filters)}`,
      options,
    ),
  );
}

async function generateAdvisorBrief(
  filters: CommandCenterFilters,
  options?: RequestOptions,
): Promise<CommandCenterActionResult> {
  return expectData(
    await apiClient.post<CommandCenterActionResult>(
      `${apiOrigin}/api/v1/dashboard/command-center/advisor-brief${queryString(filters)}`,
      undefined,
      options,
    ),
  );
}

async function exportReport(
  filters: CommandCenterFilters,
  options?: RequestOptions,
): Promise<CommandCenterExportResult> {
  return expectData(
    await apiClient.post<CommandCenterExportResult>(
      `${apiOrigin}/api/v1/dashboard/command-center/export${queryString(filters)}`,
      undefined,
      options,
    ),
  );
}

export const commandCenterClient = {
  getCommandCenter,
  generateAdvisorBrief,
  exportReport,
};
